import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { changeLocale } from '../i18n';
import { writeLocale, AppLocale } from '../utils/locale';

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'language']);
  const [saving, setSaving] = useState(false);
  const [locale, setLocaleState] = useState<AppLocale>(
    (i18n.resolvedLanguage as AppLocale) || (i18n.language as AppLocale) || 'id',
  );
  const [toast, setToast] = useState<string | null>(null);

  const handleLocaleChange = async (next: AppLocale) => {
    if (next === locale) return;
    setSaving(true);
    try {
      writeLocale(next);
      await changeLocale(next);
      setLocaleState(next);
      setToast(t('settings:save_success'));
      setTimeout(() => setToast(null), 1500);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
          {t('settings:title')}
        </h1>
        <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
          {t('settings:subtitle')}
        </p>

        <section className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <h2 className="text-base sm:text-lg font-medium text-gray-900">
              {t('settings:language_label')}
            </h2>
          </div>

          <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
            {t('settings:language_hint')}
          </p>

          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {(['id', 'tet', 'en'] as AppLocale[]).map((code) => {
              const selected = locale === code;
              const labelKey =
                code === 'en'
                  ? 'language:english'
                  : code === 'tet'
                    ? 'language:tetum'
                    : 'language:indonesian';
              return (
                <button
                  key={code}
                  type="button"
                  disabled={saving}
                  onClick={() => handleLocaleChange(code)}
                  className={`w-full flex items-center px-3 py-2.5 sm:px-4 sm:py-3 border rounded-lg transition-colors text-left ${
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                  } disabled:opacity-60`}
                >
                  <div
                    className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selected ? 'border-primary' : 'border-gray-400'
                    }`}
                  >
                    {selected && <div className="w-3 h-3 rounded-full bg-primary" />}
                  </div>
                  <span className="ml-2 text-xs sm:text-base text-gray-800">
                    {t(labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {toast && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg z-50">
            {toast}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Settings;
